import { apiSlice } from './apiSlice';
import type { CursorPage } from './directoryApi';

export interface CustomerProfile {
  id: string;
  orgId: string;
  identityId: string | null;
  displayName: string | null;
  status: string;
  mergedIntoId: string | null;
  createdBy: string;
  retentionReviewAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerShare {
  id: string;
  orgId: string;
  profileId: string;
  granteeOrgId: string;
  scopes: string[];
  expiresAt: string | null;
  grantedBy: string;
  revokedAt: string | null;
  createdAt: string;
}

interface DataResponse<T> {
  data: T;
}

export const customersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query<
      CursorPage<CustomerProfile>,
      { cursor?: string; limit?: number; search?: string; status?: string } | void
    >({
      query: (params) => ({ url: '/customers/v1/customers', params: params || undefined }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Customer' as const, id })),
              { type: 'Customer', id: 'LIST' },
            ]
          : [{ type: 'Customer', id: 'LIST' }],
    }),
    getCustomer: builder.query<CustomerProfile, string>({
      query: (id) => `/customers/v1/customers/${id}`,
      transformResponse: (response: DataResponse<CustomerProfile>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Customer', id }],
    }),
    createCustomer: builder.mutation<
      CustomerProfile,
      { displayName?: string; createdBy: 'user' | 'cashier' | 'self' | 'kiosk' }
    >({
      query: (body) => ({ url: '/customers/v1/customers', method: 'POST', body }),
      transformResponse: (response: DataResponse<CustomerProfile>) => response.data,
      invalidatesTags: [{ type: 'Customer', id: 'LIST' }],
    }),
    updateCustomer: builder.mutation<
      CustomerProfile,
      { id: string; body: { displayName?: string | null } }
    >({
      query: ({ id, body }) => ({ url: `/customers/v1/customers/${id}`, method: 'PATCH', body }),
      transformResponse: (response: DataResponse<CustomerProfile>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Customer', id },
        { type: 'Customer', id: 'LIST' },
      ],
    }),
    getCustomerShares: builder.query<
      CursorPage<CustomerShare>,
      { id: string; cursor?: string; limit?: number; status?: string; granteeOrgId?: string }
    >({
      query: ({ id, ...params }) => ({ url: `/customers/v1/customers/${id}/shares`, params }),
      providesTags: (result, _error, { id }) =>
        result
          ? [
              ...result.data.map(({ id: shareId }) => ({
                type: 'CustomerShare' as const,
                id: shareId,
              })),
              { type: 'CustomerShare', id: `CUSTOMER-${id}` },
            ]
          : [{ type: 'CustomerShare', id: `CUSTOMER-${id}` }],
    }),
    getCustomerShare: builder.query<CustomerShare, { id: string; shareId: string }>({
      query: ({ id, shareId }) => `/customers/v1/customers/${id}/shares/${shareId}`,
      transformResponse: (response: DataResponse<CustomerShare>) => response.data,
      providesTags: (_result, _error, { shareId }) => [{ type: 'CustomerShare', id: shareId }],
    }),
    grantCustomerShare: builder.mutation<
      CustomerShare,
      { id: string; granteeOrgId: string; scopes: string[]; expiresAt?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/customers/v1/customers/${id}/share`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: DataResponse<CustomerShare>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'CustomerShare', id: `CUSTOMER-${id}` },
      ],
    }),
    revokeCustomerShare: builder.mutation<{ success: boolean }, { id: string; policyId: string }>({
      query: ({ id, policyId }) => ({
        url: `/customers/v1/customers/${id}/share/${policyId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: DataResponse<{ success: boolean }>) => response.data,
      invalidatesTags: (_result, _error, { id, policyId }) => [
        { type: 'CustomerShare', id: `CUSTOMER-${id}` },
        { type: 'CustomerShare', id: policyId },
      ],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useGetCustomerSharesQuery,
  useGrantCustomerShareMutation,
  useRevokeCustomerShareMutation,
  useGetCustomerShareQuery,
} = customersApi;
